import React from "react";
import { useSelector } from "react-redux";
import Button from "../../../../components/staff/Button";
import InputText from "../../../../components/staff/InputText";
import InputTextArea from "../../../../components/staff/InputTextArea";
import RadioGroup from "../../../../components/staff/RadioGroup";
import {
  staffFieldControlSlot,
  staffFieldRoot,
} from "../../../../components/staff/fieldStyles";
import DropDownMultiple from "../../../../components/staff/DropDownMultiple";
import SaveChanges from "../../../../components/Atomos/Icons/EditMode/SaveChanges";
import TrashIcon from "../../../../components/Atomos/Icons/EditMode/Trash";
import {
  MAX_REGISTRATION_GUESTS,
} from "../../../../interfaces/models/IRegistration";
import { attendeesStatusEnum } from "../../../../Enums/RegistrationEnum";
import { formatTimestampDate, timestampToDateString } from "../../../../utils/dates";
import {
  canRemoveUnsavedGuest,
  createEmptyRegistrationGuest,
  normalizeRegistrationGuests,
} from "../../../../utils/registrationGuests";
import { useEditParticipantForm } from "../hooks/useEditParticipantForm";
import { useEditParticipantSave } from "../hooks/useEditParticipantSave";
import {
  selectIsEditMode,
  selectIsRegistrationCancelled,
  selectRegistrationData,
} from "../editParticipant.selectors";
import AddIcon from "../../../../components/Atomos/Icons/AddIcon";
import ReadOnlyField from "../../../../components/staff/ReadOnlyField";

const registrationStatusOptions = Object.entries(attendeesStatusEnum).map(
  ([key, value]) => ({
    id: String(key),
    name: value,
  }),
);

const guestStatusOptions = [
  { id: "noGuest", name: "No Guest" },
  { id: "attending", name: "Attending" },
];

function findOptionName (
  options: { id: string; name: string }[],
  selectedId?: string | null,
): string {
  if (!selectedId) {
    return "";
  }
  return options.find((option) => option.id === selectedId)?.name ?? "";
}

export default function RegistrantInformationSection (): React.ReactElement {
  const isEditMode = useSelector(selectIsEditMode);
  const registrationData = useSelector(selectRegistrationData);
  const isRegistrationCancelled = useSelector(selectIsRegistrationCancelled);
  const {
    isRegistrantModifyMode,
    setIsRegistrantModifyMode,
    registrantFieldsDisabled,
    guests,
    showGuestValidationErrors,
    registrantTypeOptions,
    organizationLabel,
    organizationOptions,
    organizationValue,
    registrantCatalogOptions,
    registrantCatalogValue,
    handleAttendeeTypeChange,
    handleOrganizationChange,
    handleRegistrantChange,
    patchRegistration,
    setGuests,
  } = useEditParticipantForm();
  const { isSaving, handleSaveRegistrantSection } = useEditParticipantSave();

  const persistedGuests = normalizeRegistrationGuests(registrationData);
  const registrationStatus = isRegistrationCancelled ? "cancelled" : "attending";
  const guestStatus = guests.length > 0 ? "attending" : "noGuest";
  const registrationModifiedAt =
    registrationData?._ts ?? registrationData?.registrationDate;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
        <h3 className="text-secondary"> Registrant Information</h3>
        {isEditMode && (
          <Button
            text={
              isRegistrantModifyMode
                ? "Save"
                : "Modify Registration"
            }
            onClick={() => {
              if (isRegistrantModifyMode) {
                void handleSaveRegistrantSection();
                return;
              }
              setIsRegistrantModifyMode(true);
            }}
            color="secondary"
            variant="filled"
            size="fixed"
            className="mt-auto"
            icon={isRegistrantModifyMode ? <SaveChanges /> : undefined}
            disabled={isSaving}
          />
        )}
      </div>


      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6  gap-4 overflow-x-hidden">
        <ReadOnlyField label="Registrant Status" value={attendeesStatusEnum[registrationStatus]} />
        <ReadOnlyField label="Registrant Date" value={registrationData?.registrationDate ? timestampToDateString(registrationData.registrationDate) : ""} />
        {isEditMode && (
          <ReadOnlyField
            label={organizationLabel}
            value={findOptionName(organizationOptions, organizationValue)}
          />
        )}
        {isEditMode && (
          <ReadOnlyField
            label="Registrant Name"
            value={findOptionName(registrantCatalogOptions, registrantCatalogValue)}
          />
        )}
        <ReadOnlyField label="Guest Status" value={guestStatusOptions.find(option => option.id === guestStatus)?.name ?? ""} />
        {isEditMode && (
          <ReadOnlyField label="Notes" value={registrationData?.note} />
        )}
        {!isEditMode && <ReadOnlyField label="Modification Date" value={typeof registrationModifiedAt === "number" ? formatTimestampDate(registrationModifiedAt) : ""} />}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        {isEditMode && <ReadOnlyField label="Modification Date" value={typeof registrationModifiedAt === "number" ? formatTimestampDate(registrationModifiedAt) : ""} />}
        <DropDownMultiple
          title="Registrant Type"
          required={!isEditMode}
          options={registrantTypeOptions}
          value={registrationData?.attendeeType ?? null}
          onChange={handleAttendeeTypeChange}
          disabled={isEditMode}
          showAsLabel={isEditMode}
        />
        {!isEditMode && (
          <DropDownMultiple
            title={organizationLabel}
            required
            options={organizationOptions}
            value={organizationValue}
            onChange={handleOrganizationChange}
            disabled={registrantFieldsDisabled}
            showAsLabel={registrantFieldsDisabled}
          />
        )}
        {!isEditMode && (
          <DropDownMultiple
            title="Registrant Name"
            required
            options={registrantCatalogOptions}
            value={registrantCatalogValue}
            onChange={handleRegistrantChange}
            disabled={registrantFieldsDisabled || !organizationValue}
            showAsLabel={registrantFieldsDisabled}
          />
        )}
        <InputText
          title="Badge Name"
          type="text"
          value={registrationData?.badgeName ?? ""}
          onChange={(value) => patchRegistration({ badgeName: value })}
          disabled={registrantFieldsDisabled}
          showAsLabel={registrantFieldsDisabled}
        />
        <InputTextArea
          title="Special Requests"
          value={registrationData?.specialRequests ?? ""}
          onChange={(value) =>
            patchRegistration({ specialRequests: value })
          }
          disabled={registrantFieldsDisabled}
          showAsLabel={registrantFieldsDisabled}
          className="!h-fit"
        />
        {!isEditMode && (
          <InputTextArea
            title="Notes"
            value={registrationData?.note ?? ""}
            onChange={(value) => patchRegistration({ note: value })}
            className="!h-fit"
          />
        )}
        {!(registrantFieldsDisabled || guests.length >= MAX_REGISTRATION_GUESTS) &&
          <Button
            text="Add Guest"
            onClick={() => {
              setGuests((prev) => {
                if (prev.length >= MAX_REGISTRATION_GUESTS) return prev;
                return [
                  ...prev,
                  createEmptyRegistrationGuest(
                    registrationData?.registrationDate,
                  ),
                ];
              });
            }}
            color="secondary"
            variant="outlined"
            size="fixed"
            className="mt-auto"
            icon={<AddIcon />}
          />
        }
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 border-t border-gray-200 pt-4">
        {guests.map((guest, index) => (
          <div key={guest.id} className="grid md:flex gap-4 h-fit">
            <ReadOnlyField label="Modification Date" value={formatTimestampDate(guest.modificationDate ?? guest.registrationDate)} />
            <InputText
              title="Guest Name"
              required
              value={guest.name}
              onChange={(value) => {
                setGuests((prev) =>
                  prev.map((g, i) =>
                    i === index ? { ...g, name: value } : g,
                  ),
                );
              }}
              disabled={registrantFieldsDisabled}
              showAsLabel={registrantFieldsDisabled}
              error={showGuestValidationErrors && !guest.name.trim()}
              errorMessage="Guest name is required."
              className="flex-1"
            />
            <InputText
              title="Special Requests"
              value={guest.specialRequest ?? ""}
              onChange={(value) => {
                setGuests((prev) =>
                  prev.map((g, i) =>
                    i === index ? { ...g, specialRequest: value } : g,
                  ),
                );
              }}
              disabled={registrantFieldsDisabled}
              showAsLabel={registrantFieldsDisabled}
              className="flex-1"
            />
            {!registrantFieldsDisabled &&
              canRemoveUnsavedGuest(guest.id, persistedGuests) && (
              <Button
                onClick={() => {
                  setGuests((prev) => prev.filter((_, i) => i !== index));
                }}
                color="primary"
                variant="filled"
                size="fixed"
                className="mt-auto !w-fit"
                icon={<TrashIcon className="w-4 h-4 text-white" />}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
